import { Stack, Typography } from "@mui/material";
import { StopEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/stopEmailCycleRPC";
import {
  Dialog,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { HelpCircle } from "lucide-react";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";
import { SuccessDialog } from "../../../../components/dialogs/SuccessDialog";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { useStopEmailCycleStore } from "../../stores/Hooks";

interface StopEmailCycleDialogProps {
  handleDialogClose: (refetchTable?: boolean) => void;
}

type DialogToOpen =
  | "CONFIRM_STOP"
  | "SUCCESS"
  | "CYCLE_ALREADY_LAUNCHED_ERROR"
  | "UNEXPECTED_ERROR";

const CONFIRM_STOP_DIALOG_DESCRIPTION_ICON = 20;
const CONFIRM_STOP_DIALOG_WIDTH = "560px";

export const StopEmailCycleDialog: React.FC<StopEmailCycleDialogProps> =
  observer(({ handleDialogClose }) => {
    const [dialogToOpen, setDialogToOpen] =
      useState<DialogToOpen>("CONFIRM_STOP");

    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const tokens = useFoundationColorTokens();
    const navigateTo = useRouteNavigator();
    const stopEmailCycleStore = useStopEmailCycleStore();

    const handleStopEmailCycle = async (): Promise<void> => {
      try {
        await stopEmailCycleStore.stopEmailCycle();

        switch (stopEmailCycleStore.stopEmailCycleRPCError) {
          case null:
            setDialogToOpen("SUCCESS");
            break;
          case StopEmailCycleRPC.RPCError.InvalidScheduleId:
            navigateTo.pageNotFound();
            break;
          case StopEmailCycleRPC.RPCError.ScheduleNotInProgress:
            setDialogToOpen("CYCLE_ALREADY_LAUNCHED_ERROR");
            break;
          default:
            stopEmailCycleStore.stopEmailCycleRPCError satisfies never;
        }
      } catch {
        setDialogToOpen("UNEXPECTED_ERROR");
      }
    };

    const handleClose = (refetchTable = true): void => {
      handleDialogClose(refetchTable);
    };

    return (
      <>
        <Dialog
          open={dialogToOpen === "CONFIRM_STOP"}
          title={t("emailCycles.stopCycleDialogTitle")}
          primaryButtonText={t("emailCycles.stopCycle")}
          primaryButtonColor="destructive"
          onPrimaryButtonClick={handleStopEmailCycle}
          secondaryButtonText={t("common.cancel")}
          onSecondaryButtonClick={() => handleClose(false)}
          isSecondaryButtonDisabled={stopEmailCycleStore.loadingStopEmailCycle}
          onClose={stopEmailCycleStore.resetStore}
          width={CONFIRM_STOP_DIALOG_WIDTH}
          disableBackdropClick={true}
        >
          <Stack
            direction="row"
            color={tokens.labelError}
            gap={spacing.spaceXS}
          >
            <HelpCircle
              size={CONFIRM_STOP_DIALOG_DESCRIPTION_ICON}
              style={{ flexShrink: 0 }}
            />
            <Typography {...typography.b2}>
              {t("emailCycles.confirmStopCycleDialogDescription")}
            </Typography>
          </Stack>
        </Dialog>

        {dialogToOpen === "SUCCESS" && (
          <SuccessDialog
            title={t("emailCycles.stopEmailCycleSuccessDialogTitle")}
            message={t("emailCycles.stopEmailCycleSuccessDialogDescription")}
            onClickClose={handleClose}
          />
        )}

        {dialogToOpen === "CYCLE_ALREADY_LAUNCHED_ERROR" && (
          <ErrorDialog
            title={t("emailCycles.stopCycleFailed")}
            message={t("emailCycles.stopCycleAlreadyLaunchedErrorMessage")}
            onClickClose={handleClose}
          />
        )}

        {dialogToOpen === "UNEXPECTED_ERROR" && (
          <UnexpectedErrorDialog
            message={t(
              "emailCycles.stopEmailCycleUnexpectedErrorDialogDescription",
            )}
            onClickDone={handleClose}
          />
        )}
      </>
    );
  });
